import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import DashboardTable from '../../components/DashboardTable';
import DashboardPageHeader from '../../components/DashboardPageHeader';
import PageStack from '../../components/PageStack';
import { LAYOUT } from '../../theme/layoutTokens';
import Stack from '@mui/joy/Stack';
import CircularProgress from '@mui/joy/CircularProgress';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { brandsApi } from '../../api/brands.api';
import { Brand } from '../../types/brand';

const BrandList: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);

  const loadBrands = useCallback(async () => {
    try {
      const data = await brandsApi.getAll();
      setBrands(data);
    } catch (error) {
      console.error('Error al cargar marcas:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  const handleDelete = async (reference: string) => {
    if (window.confirm('¿Estás seguro de eliminar esta marca?')) {
      try {
        await brandsApi.delete(reference);
        setBrands((prev) => prev.filter((brand) => brand.reference !== reference));
      } catch (error) {
        console.error('Error al eliminar marca:', error);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageStack spacing={LAYOUT.stackSurfaceGap}>
      <DashboardPageHeader
        title="Marcas"
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button component={RouterLink} to="/brands/new" color="primary">
              Nueva marca
            </Button>
            <Button component={RouterLink} to="/brands/bulk" color="success" variant="soft">
              Creación masiva
            </Button>
          </Stack>
        }
      />

      <Sheet variant="outlined" sx={{ overflow: 'auto', borderRadius: 'md', boxShadow: 'sm', p: 0 }}>
        <DashboardTable stickyHeader>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Descripción</th>
              <th>Logo</th>
              <th>Fecha de creación</th>
              <th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {brands.map((brand) => (
              <tr key={brand.reference}>
                <td>{brand.name}</td>
                <td>{brand.description || '—'}</td>
                <td>
                  {brand.logo ? (
                    <Box
                      component="img"
                      src={brand.logo}
                      alt=""
                      sx={{ height: 32, width: 'auto', maxWidth: 120, objectFit: 'contain' }}
                    />
                  ) : (
                    '—'
                  )}
                </td>
                <td>{new Date(brand.createdAt).toLocaleDateString()}</td>
                <td>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                    <IconButton
                      component={RouterLink}
                      to={`/brands/${brand.reference}/edit`}
                      size="sm"
                      variant="plain"
                      color="primary"
                      title="Editar marca"
                    >
                      <EditRoundedIcon />
                    </IconButton>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={() => handleDelete(brand.reference)}
                      title="Eliminar marca"
                    >
                      <DeleteRoundedIcon />
                    </IconButton>
                  </Stack>
                </td>
              </tr>
            ))}
          </tbody>
        </DashboardTable>
      </Sheet>
    </PageStack>
  );
};

export default BrandList;
